import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Props {
  moduleId: string;
  xpEarned: number;
  score?: number;       // 0–100 percentage
  scoreLabel?: string;  // e.g. "Accuracy", "Privacy Score"
}

// Key concepts shown per module on the takeaway card
const MODULE_INFO: Record<string, {
  title: string;
  accentColor: string;
  concepts: string[];
}> = {
  quest: {
    title: 'Privacy Quest',
    accentColor: '#4f46e5',
    concepts: [
      'Every digital choice creates a data trail companies can monetise.',
      'Default app settings are rarely the most privacy-protective option.',
      'Under PIPEDA, Canadians have the right to access and delete their personal data.',
      'Small data points combine into detailed personal profiles.',
      'Privacy trade-offs are real — convenience often costs data.',
    ],
  },
  algorithm: {
    title: 'Algorithm Mirror',
    accentColor: '#059669',
    concepts: [
      'Recommendation algorithms build a profile from every click, scroll, and pause.',
      'You can be profiled by content type, timing, and engagement patterns — no login needed.',
      'Platforms infer income, relationships, politics, and mood from behaviour.',
      'Turning off personalised ads and using uBlock Origin meaningfully limits tracking.',
      'Requesting data deletion under PIPEDA is a real, enforceable right.',
    ],
  },
  dataheist: {
    title: 'Data Heist',
    accentColor: '#d97706',
    concepts: [
      'Dark patterns are UI designs deliberately engineered to extract your data.',
      'Pre-ticked consent boxes and buried opt-outs violate the spirit of PIPEDA.',
      'Corporations use urgency, social proof, and confusion to bypass informed consent.',
      'Recognising dark patterns lets you refuse them before handing over data.',
      'Defenders and regulators use the same pattern vocabulary to build enforcement cases.',
    ],
  },
  phantom: {
    title: 'Phantom Footprint',
    accentColor: '#00b894',
    concepts: [
      'An ordinary day generates dozens of data points across unrelated services.',
      'Passive data (location pings, Bluetooth signals, sensor readings) is invisible but persistent.',
      'Data brokers aggregate signals from multiple sources into a single profile.',
      'Privacy mode meaningfully reduces — but does not eliminate — your footprint.',
      'Minimising data sharing at each step compounds into significant privacy protection.',
    ],
  },
  permissions: {
    title: 'Permission Impossible',
    accentColor: '#4f46e5',
    concepts: [
      '"Allow While Using" is nearly always safer than "Always Allow" for location.',
      'A flashlight app has no legitimate reason to access your contacts or microphone.',
      'Permission creep occurs when apps request access they don\'t functionally need.',
      'You can revoke permissions any time in your phone\'s Settings without losing core app function.',
      'Risk scales with the sensitivity of the permission and how broadly you grant it.',
    ],
  },
  trial: {
    title: 'Terms & Conditions: The Trial',
    accentColor: '#7c3aed',
    concepts: [
      'Perpetual, irrevocable licences mean a company can use your content forever.',
      'Vague "partners" clauses allow data sharing far beyond what users expect.',
      'Indefinite data retention is incompatible with PIPEDA\'s purpose-limitation principle.',
      'Opt-out defaults shift the burden of privacy protection onto the user.',
      'Even paraphrased privacy policies contain legally significant distinctions.',
    ],
  },
  leaklab: {
    title: 'The Leak Lab',
    accentColor: '#dc2626',
    concepts: [
      'Containment speed directly limits breach scope — every minute of delay expands exposure.',
      'Wrong containment actions (e.g., wiping logs) can compromise forensic evidence.',
      'PIPEDA breach reporting obligations are triggered by "real risk of significant harm".',
      'Disclosure notifications must be specific, timely, and actionable for affected individuals.',
      'Insider threat, unpatched CVEs, and misconfigured storage are the three most common breach vectors.',
    ],
  },
  detective: {
    title: 'Deepfake Detective',
    accentColor: '#be123c',
    concepts: [
      'AI-generated media contains forensic artefacts: unnatural blinking, lighting inconsistencies, and audio spectral anomalies.',
      'Facial geometry synthesis errors are most visible at hairlines, ears, and teeth.',
      'Deepfakes are used for fraud, non-consensual imagery, and political disinformation.',
      'Reverse image search and metadata inspection are your first verification tools.',
      'Healthy scepticism toward viral media is a core digital literacy skill.',
    ],
  },
};

export default function ModuleSummary({ moduleId, xpEarned, score, scoreLabel }: Props) {
  const navigate = useNavigate();
  const info = MODULE_INFO[moduleId];
  if (!info) return null;

  const handlePrint = () => {
    const date = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
    const scoreBlock = score !== undefined
      ? `<div class="score-row"><span class="score-label">${scoreLabel ?? 'Score'}</span><span class="score-value">${score}%</span></div>`
      : '';
    const conceptsList = info.concepts
      .map(c => `<li>${c}</li>`)
      .join('\n');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Privacy Education — ${info.title} Summary</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1e1b4b; padding: 40px; max-width: 680px; margin: 0 auto; }
    .header { border-bottom: 3px solid ${info.accentColor}; padding-bottom: 16px; margin-bottom: 24px; }
    .brand { font-size: 13px; font-weight: 700; color: #6b7280; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 8px; }
    h1 { font-size: 28px; font-weight: 800; color: #1e1b4b; }
    .date { font-size: 13px; color: #6b7280; margin-top: 4px; }
    .stats { display: flex; gap: 24px; margin-bottom: 28px; }
    .stat { background: #f3f4f6; border-radius: 12px; padding: 16px 20px; }
    .stat-value { font-size: 26px; font-weight: 800; color: ${info.accentColor}; }
    .stat-label { font-size: 12px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; margin-top: 2px; }
    .score-row { background: #f3f4f6; border-radius: 12px; padding: 16px 20px; }
    .score-label { font-size: 12px; color: #6b7280; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; display: block; margin-bottom: 2px; }
    .score-value { font-size: 26px; font-weight: 800; color: ${info.accentColor}; }
    h2 { font-size: 16px; font-weight: 700; color: #374151; margin-bottom: 12px; }
    ul { padding-left: 20px; }
    li { font-size: 14px; line-height: 1.8; color: #374151; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af; }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">privacy.education</div>
    <h1>${info.title}</h1>
    <div class="date">Completed ${date}</div>
  </div>
  <div class="stats">
    <div class="stat">
      <div class="stat-value">+${xpEarned} XP</div>
      <div class="stat-label">Earned</div>
    </div>
    ${scoreBlock}
  </div>
  <h2>Key concepts from this module</h2>
  <ul>${conceptsList}</ul>
  <div class="footer">Generated by privacy.education &mdash; Canada&apos;s interactive privacy literacy platform</div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 400);
    }
  };

  return (
    <div style={{
      background: 'white',
      border: `2px solid ${info.accentColor}`,
      borderRadius: 20,
      padding: 28,
      marginTop: 32,
      maxWidth: 600,
      marginLeft: 'auto',
      marginRight: 'auto',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: info.accentColor,
        letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12,
      }}>
        Module takeaway
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 800, color: '#1e1b4b', marginBottom: 16, marginTop: 0 }}>
        What you learned in {info.title}
      </h3>

      <ul style={{ paddingLeft: 20, marginBottom: 20 }}>
        {info.concepts.map((c, i) => (
          <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.8, marginBottom: 4 }}>
            {c}
          </li>
        ))}
      </ul>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={handlePrint}
          style={{
            background: info.accentColor, color: 'white', border: 'none',
            borderRadius: 10, padding: '10px 20px', fontSize: 14,
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          Download / Print Summary
        </button>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'white', color: '#6b7280',
            border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Back to home
        </button>
      </div>
    </div>
  );
}
